import { Logic } from 'logic/logic';
import moment from 'moment';
import { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { Subscription } from 'rxjs';
import i18n from 'i18next';

import { DriverBehaviorTrendsModel } from 'common/model/statistics';
import { RouteNames } from 'App';

import { DriverBehaviorType } from '../../../components/DriverBehaviorBar';
import { DocsUserGuide } from 'modules/docs/DocsModule';
import { confDefault } from 'conf';
import HelperModal from 'common/components/HelperModal';
import DriverBehaviorBar from '../../../components/DriverBehaviorBar';
import { searched } from 'common/utils/search';
import { ReadOnlyClientFleetTypeEnum } from 'generated/new-main';
import DriverBehaviorTrendsTable from '../../../components/DriverBehaviorTrendsTable';
import { DriverLeaderboardTrendsTableFilter } from '../../../components/DriverLeaderboardTrendsTable';
import qs from 'qs';
import { DATE_FORMAT } from 'domain-constants';

interface Props extends RouteComponentProps, WithTranslation {
    logic: Logic;
    leaderFilter: DriverLeaderboardTrendsTableFilter;
}

interface State {
    data: DriverBehaviorTrendsModel[];
    search: string;
    date: string;
    filter: DriverBehaviorType;
    loading: boolean;
    export: {
        processing: boolean;
        enable: boolean;
    };
    helper?: {
        content: string;
    };
}

class DriverListModule extends Component<Props, State> {
    subscriptionLoading?: Subscription;
    subscriptionData?: Subscription;
    subscriptionExport?: Subscription;

    constructor(props: Props) {
        super(props);
        this.state = {
            data: [],
            search: '',
            filter: DriverBehaviorType.Trucks,
            loading: true,
            export: {
                enable: false,
                processing: false
            },
            date: moment(moment.utc(this.props.logic.settings().getProp('driverBehavior').date).toLocaleString())
                .startOf('month')
                .toISOString()
        };
        this.props.logic.driverBehaviorCoach().trucks().setDate(this.state.date);
    }

    componentDidMount() {
        if (this.props.logic.driverBehaviorCoach().trucks().showPromo()) {
            this.props.history.push(RouteNames.STATISTICS_DRIVER_BEHAVIOR);
            return;
        }

        this.subscriptionData = this.props.logic
            .driverBehaviorCoach()
            .trucks()
            .dataTrends.subscribe(data => {
                this.setState(state => ({
                    data,
                    export: {
                        ...state.export,
                        enable: true
                    }
                }));
            });

        this.subscriptionLoading = this.props.logic
            .driverBehaviorCoach()
            .trucks()
            .loading.subscribe(loading => {
                this.setState({ loading });
            });

        this.subscriptionExport = this.props.logic
            .driverBehaviorCoach()
            .trucks()
            .exportListProcessing.subscribe(processing => {
                this.setState(state => ({
                    export: {
                        ...state.export,
                        processing: processing
                    }
                }));
            });

        this.props.logic.driverBehaviorCoach().trucks().loadPerfectDriveScoreData(this.state.date);
    }

    componentWillUnmount() {
        this.subscriptionLoading?.unsubscribe();
        this.subscriptionData?.unsubscribe();
        this.subscriptionExport?.unsubscribe();
    }

    render() {
        const { data, search, loading } = this.state;
        return (
            <>
                <DriverBehaviorBar
                    date={this.state.date}
                    filter={this.state.filter}
                    searchEnabled={this.state.data.length > 0}
                    onDateRangeChange={this._onDateRangeChange}
                    exportEnable={this.state.export.enable}
                    exportProcessing={this.state.export.processing}
                    clientFleetType={
                        this.props.logic.auth().newEWClient()?.fleetType ?? ReadOnlyClientFleetTypeEnum.Mixed
                    }
                    onSearch={this._onSearch}
                    onFilterChange={this._onFilterChange}
                    onExportClick={this._onExportClick}
                    onHelperClick={this._onBarHelperClick}
                    onBarDateNextClick={this._onBarDateNextClick}
                    onBarDatePreviousClick={this._onBarDatePreviousClick}
                />
                <DriverBehaviorTrendsTable
                    data={data.filter(d => searched(search, d.name)).map((data, key) => ({ ...data, key }))}
                    loading={loading}
                    onRowClick={this._onTableRowClick}
                />
                <HelperModal
                    name="driver-behavior"
                    content={this.state.helper?.content ?? ''}
                    onClose={this._onHelperClose}
                    visible={!!this.state.helper}
                />
            </>
        );
    }

    private _onExportClick = () => {
        this.props.logic.driverBehaviorCoach().trucks().downloadDriverBehaviorListExport();
    };

    private _onTableRowClick = (id: string, name?: string) => {
        this.props.logic.driverBehaviorCoach().trucks().setSelectedDriverId(id);
        this.props.logic.driverBehaviorCoach().trucks().setSelectedDriverName(name);
        this.props.history.push({
            pathname: RouteNames.STATISTICS_DRIVER_BEHAVIOR_TRUCKS_DETAIL + '/' + id,
            search: qs.stringify({
                date: moment(this.props.logic.driverBehaviorCoach().trucks().getDate())
                    .startOf('month')
                    .format(DATE_FORMAT)
            })
        });
    };

    private _onSearch = (search: string) => {
        this.setState({ search });
    };

    private _onDateRangeChange = (date: string) => {
        this.setState({ date, search: '', data: [] }, () => {
            this.props.logic.driverBehaviorCoach().trucks().saveDateToSettings(date);
            this.props.logic.driverBehaviorCoach().trucks().loadPerfectDriveScoreData(this.state.date);
        });
    };

    private _onBarDatePreviousClick = () => {
        const date = moment(this.state.date).subtract(1, 'month').toISOString();
        this.setState(
            {
                date
            },
            () => {
                this.props.logic.driverBehaviorCoach().trucks().saveDateToSettings(date);
                this.props.logic.driverBehaviorCoach().trucks().loadPerfectDriveScoreData(date);
            }
        );
    };

    private _onBarDateNextClick = () => {
        const date = moment(this.state.date).add(1, 'month').toISOString();
        this.setState(
            {
                date
            },
            () => {
                this.props.logic.driverBehaviorCoach().trucks().saveDateToSettings(date);
                this.props.logic.driverBehaviorCoach().trucks().loadPerfectDriveScoreData(date);
            }
        );
    };

    private _onFilterChange = (filter: DriverBehaviorType) => {
        if (filter === DriverBehaviorType.Light) {
            this.props.history.push(RouteNames.STATISTICS_DRIVER_BEHAVIOR_VEHICLES);
        } else {
            this.setState({ filter });
        }
    };

    private _onBarHelperClick = () => {
        const module: DocsUserGuide = 'driverbehaviourcoach';

        const language = confDefault.langsDocs.includes(i18n.language) ? i18n.language : 'en';

        fetch(`${this.props.logic.conf.docs.path}${language}/${module}.html`).then(response => {
            response.text().then(content => {
                this.setState({
                    helper: {
                        content
                    }
                });
            });
        });
    };

    private _onHelperClose = () => {
        this.setState({
            helper: undefined
        });
    };
}

export default withRouter(withTranslation()(DriverListModule));
